import { Button, Select, type SelectOption } from '@civilgrid/ui/components';
import type { AreaUnits, Units } from '@turf/turf';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { EditorMode, type UnitSystem } from '~/common/types';
import type { BasicMapStore } from '../..';
import { areaUnits, clearMeasurement, lengthUnits } from './common';
import { areaStore, lengthStore } from './store';

export interface DropdownProps {
  align: 'end' | 'start' | 'center' | undefined;
  sideOffset: number;
  alignOffset: number;
}

export default function MeasurementDisplay({ store }: { store: BasicMapStore }) {
  const { editorMode } = useSnapshot(store);
  const l = useSnapshot(lengthStore);
  const a = useSnapshot(areaStore);

  const newMeasurement = () => {
    clearMeasurement(store);
    setTimeout(() => {
      store.setEditorMode(editorMode);
    }, 0);
  };

  return (
    <div className="shadow-bg-grey-70 absolute bottom-4 left-12 z-10 flex min-w-[20rem] flex-col gap-4 rounded-sm bg-white p-2">
      <div className="m-1 flex min-w-[10rem] flex-col text-sm">
        <div className="text-2xs uppercase">Measurements: </div>
        {editorMode === EditorMode.DrawMeasureDistance && (
          <div>
            Length: {l.length.toLocaleString()} {l.unitType}
          </div>
        )}

        {editorMode === EditorMode.DrawMeasureArea && (
          <div className="flex flex-col">
            <div>
              Perimeter: {a.length.toLocaleString()} {a.unitType === 'acres' ? 'feet' : a.unitType}
            </div>
            <div>
              Area: {a.area.toLocaleString()} {a.unitType}
            </div>
          </div>
        )}
      </div>

      <UnitSelect store={store} />

      <div className="flex flex-col gap-1">
        <Button onClick={() => newMeasurement()}>Restart</Button>
        <Button variant="secondary" onClick={() => clearMeasurement(store)}>
          Exit
        </Button>
      </div>
    </div>
  );
}

const UnitSelect = ({ store }: { store: BasicMapStore }) => {
  const { editorMode } = useSnapshot(store) as typeof store;

  const handleLengthUnitChange = (type: Units, system: UnitSystem) => {
    lengthStore.unitType = type;
    lengthStore.unitSystem = system;
  };

  const handleAreaUnitChange = (type: AreaUnits, system: UnitSystem) => {
    areaStore.unitType = type;
    areaStore.unitSystem = system;
  };

  const unitItemsAsArray: SelectOption[] = useMemo(() => {
    if (editorMode === EditorMode.DrawMeasureDistance) {
      return lengthUnits.map((u) => ({
        label: u.label,
        value: u.unitType,
      }));
    }

    if (editorMode === EditorMode.DrawMeasureArea) {
      return areaUnits.map((u) => ({
        label: u.label,
        value: u.unitType,
      }));
    }

    return [];
  }, [editorMode]);

  if (editorMode === EditorMode.DrawMeasureDistance) {
    return (
      <Select
        label="Units"
        options={unitItemsAsArray}
        value={lengthStore.unitType}
        onValueChange={(unitType) => {
          if (unitType) handleLengthUnitChange(unitType as Units, lengthStore.unitSystem);
        }}
        tooltipText="Select units to measure length"
        data-testid="measure-length-units-select"
      />
    );
  }

  if (editorMode === EditorMode.DrawMeasureArea) {
    return (
      <Select
        label="Units"
        options={unitItemsAsArray}
        value={areaStore.unitType}
        onValueChange={(unitType) => {
          if (unitType) handleAreaUnitChange(unitType as AreaUnits, areaStore.unitSystem);
        }}
        tooltipText="Select units to measure area"
        data-testid="measure-area-units-select"
      />
    );
  }

  return null;
};
